import { useState, useEffect } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';

export default function ApiSettings() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('keys');
  const [config, setConfig] = useState({
    api_mode: 'mingxi',
    allow_rpa_fetch: false,
    api_text_provider: 'deepseek',
    api_image_provider: 'doubao',
    api_keys: {}
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  
  // Track which fields are in "edit" mode
  const [editingFields, setEditingFields] = useState({});
  // Temporary state for the field being edited before saving
  const [tempValues, setTempValues] = useState({});

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/system-config?_t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store'
      });
      const data = await res.json();
      if (!data.api_keys) data.api_keys = {};
      setConfig(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyChange = (provider, key) => {
    setConfig(prev => ({
      ...prev,
      api_keys: { ...prev.api_keys, [provider]: key }
    }));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      const res = await fetch(`${API_URL}/admin/system-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(config)
      });
      const data = await res.json();
      setMessage('保存成功 / Saved successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error(err);
      setMessage('保存失败 / Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (fieldKey) => {
    setEditingFields({ ...editingFields, [fieldKey]: true });
    setTempValues({ ...tempValues, [fieldKey]: '' }); // Empty input
  };

  const cancelEditing = (fieldKey) => {
    setEditingFields({ ...editingFields, [fieldKey]: false });
  };

  const saveSingleField = async (fieldKey) => {
    // Update config state with the new temp value
    const newConfig = {
      ...config,
      api_keys: { ...config.api_keys, [fieldKey]: tempValues[fieldKey] }
    };
    setConfig(newConfig);
    
    // Call API to save immediately
    setSaving(true);
    setMessage('');
    try {
      const res = await fetch(`${API_URL}/admin/system-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newConfig)
      });
      await res.json();
      setMessage('保存成功 / Saved successfully');
      setTimeout(() => setMessage(''), 3000);
      setEditingFields({ ...editingFields, [fieldKey]: false });
    } catch (err) {
      console.error(err);
      setMessage('保存失败 / Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-4">加载中...</div>;

  const keyFields = [
    { key: 'deepseek', label: 'DeepSeek API Key', isMasked: false },
    { key: 'doubao', label: 'Doubao API Key', isMasked: false },
    { key: 'doubao_text_ep_id', label: 'Doubao 文字推理接入点 (Endpoint ID)', isMasked: false },
    { key: 'doubao_image_ep_id', label: 'Doubao 图像推理接入点 (Endpoint ID)', isMasked: false },
    { key: 'kimi', label: 'Kimi API Key', isMasked: false },
    { key: 'qwen', label: 'Qwen API Key', isMasked: false },
    { key: 'yuanbao', label: '腾讯元宝 API Key', isMasked: false },
    { key: 'wenxin', label: '百度文心一言 API Key', isMasked: false }
  ];


  const maskApiKey = (key) => {
    if (!key || key === '未设置') return key;
    if (key.length <= 8) return '********';
    return `${key.slice(0, 4)}****${key.slice(-4)}`;
  };

  return (

    <div className="p-6 bg-white dark:bg-slate-800/50 border border-gray-100 dark:border-slate-700 rounded-lg shadow-sm">
      <h2 className="text-xl font-bold mb-6 text-gray-800 dark:text-gray-100">API 接口管理</h2>
      {message && (
        <div className={`p-3 mb-4 rounded ${message.includes('失败') ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
          {message}
        </div>
      )}

      <div className="mb-6 border-b border-gray-200 dark:border-slate-700">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('keys')}
            className={`${
              activeTab === 'keys'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-slate-500'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            API Keys 配置
          </button>
          <button
            onClick={() => setActiveTab('selection')}
            className={`${
              activeTab === 'selection'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-slate-500'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            API 接口选择
          </button>
        </nav>
      </div>

      <div className="space-y-6 max-w-2xl">
        {activeTab === 'selection' && (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-gray-50 dark:bg-slate-700/30 p-4 rounded-md">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">生成接口模式</label>
              <div className="flex items-center space-x-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio text-blue-600"
                    checked={config.api_mode === 'mingxi'}
                    onChange={() => setConfig({ ...config, api_mode: 'mingxi' })}
                  />
                  <span className="ml-2 dark:text-gray-200">明悉 API (默认集成)</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio text-blue-600"
                    checked={config.api_mode === 'zhimo'}
                    onChange={() => setConfig({ ...config, api_mode: 'zhimo' })}
                  />
                  <span className="ml-2 dark:text-gray-200">智墨多模态接口 (灵活切换)</span>
                </label>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                {config.api_mode === 'mingxi' ? '使用明悉平台集成的默认 API 进行内容生成。' : '允许分别为文本生成和图像生成选择不同的底层模型。'}
              </p>
            </div>

            {config.api_mode === 'zhimo' && (
              <div className="space-y-4 border p-4 rounded-md">
                <h3 className="font-semibold text-gray-700 dark:text-gray-300">智墨接口配置</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">文本生成模型</label>
                  <select
                    className="w-full border-gray-300 dark:border-slate-600 dark:bg-slate-700/50 dark:text-gray-200 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 p-2 border"
                    value={config.api_text_provider || 'deepseek'}
                    onChange={(e) => setConfig({ ...config, api_text_provider: e.target.value })}
                  >
                    <option className="bg-white text-gray-900 dark:bg-slate-800 dark:text-gray-200" value="deepseek">DeepSeek (深度求索)</option>
                    <option className="bg-white text-gray-900 dark:bg-slate-800 dark:text-gray-200" value="doubao">Doubao (字节豆包)</option>
                    <option className="bg-white text-gray-900 dark:bg-slate-800 dark:text-gray-200" value="kimi">Kimi (月之暗面)</option>
                    <option className="bg-white text-gray-900 dark:bg-slate-800 dark:text-gray-200" value="qwen">Qwen (通义千问)</option>
                    <option className="bg-white text-gray-900 dark:bg-slate-800 dark:text-gray-200" value="yuanbao">腾讯元宝</option>
                    <option className="bg-white text-gray-900 dark:bg-slate-800 dark:text-gray-200" value="wenxin">百度文心一言</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">图像生成模型</label>
                  <select
                    className="w-full border-gray-300 dark:border-slate-600 dark:bg-slate-700/50 dark:text-gray-200 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 p-2 border"
                    value={config.api_image_provider || 'doubao'}
                    onChange={(e) => setConfig({ ...config, api_image_provider: e.target.value })}
                  >
                    <option className="bg-white text-gray-900 dark:bg-slate-800 dark:text-gray-200" value="doubao">Doubao (字节豆包文生图)</option>
                    <option className="bg-white text-gray-900 dark:bg-slate-800 dark:text-gray-200" value="kimi">Kimi (月之暗面)</option>
                    <option className="bg-white text-gray-900 dark:bg-slate-800 dark:text-gray-200" value="qwen">Qwen (通义千问)</option>
                    <option className="bg-white text-gray-900 dark:bg-slate-800 dark:text-gray-200" value="yuanbao">腾讯元宝</option>
                    <option className="bg-white text-gray-900 dark:bg-slate-800 dark:text-gray-200" value="wenxin">百度文心一言</option>
                  </select>
                </div>
              </div>
            )}
            

            <div className="bg-gray-50 dark:bg-slate-700/30 p-4 rounded-md mt-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">明悉 RPA 深度抓取权限</label>
              <div className="flex items-center space-x-4">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="form-checkbox text-blue-600 h-5 w-5 rounded border-gray-300 focus:ring-blue-500"
                    checked={config.allow_rpa_fetch === true || config.allow_rpa_fetch === 'true'}
                    onChange={(e) => setConfig({ ...config, allow_rpa_fetch: e.target.checked })}
                  />
                  <span className="ml-2 dark:text-gray-200">允许创建 GEO 任务时使用 RPA 深度节点抓取</span>
                </label>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                开启后，用户在创建任务时可选择「RPA模式」，通过真实浏览器节点进行深度抓取。请确保后端已配置相关集群。
              </p>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {saving ? '保存中...' : '保存配置'}
            </button>
          </form>
        )}

        {activeTab === 'keys' && (
          <div className="space-y-4 border p-4 rounded-md">
            <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300 mb-3">API Keys 录入</h4>
            
            <div className="space-y-5">
              {keyFields.map((field) => {
                const isEditing = editingFields[field.key];
                const currentValue = config.api_keys?.[field.key] || '未设置';
                
                return (
                  <div key={field.key} className="pb-3 border-b border-gray-100 dark:border-slate-700 last:border-0">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{field.label}</label>
                    
                    {!isEditing ? (
                      <div className="flex items-center justify-between bg-gray-50 dark:bg-slate-700/30 p-3 rounded">
                        <span className="text-sm text-gray-600 dark:text-gray-400 break-all font-mono">
                          当前的: {maskApiKey(currentValue)}
                        </span>
                        <button
                          type="button"
                          onClick={() => startEditing(field.key)}
                          className="ml-4 text-sm text-blue-600 hover:text-blue-800 whitespace-nowrap"
                        >
                          修改
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          className="flex-1 text-sm border-gray-300 dark:border-slate-600 dark:bg-slate-700/50 dark:text-gray-200 rounded-md p-2 border focus:ring-blue-500 focus:border-blue-500"
                          placeholder="请输入新的值..."
                          value={tempValues[field.key] || ''}
                          onChange={(e) => setTempValues({ ...tempValues, [field.key]: e.target.value })}
                        />
                        <button
                          type="button"
                          onClick={() => saveSingleField(field.key)}
                          disabled={saving}
                          className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 disabled:opacity-50"
                        >
                          更新
                        </button>
                        <button
                          type="button"
                          onClick={() => cancelEditing(field.key)}
                          className="bg-gray-200 dark:bg-slate-600 text-gray-700 dark:text-gray-200 dark:text-gray-300 px-4 py-2 rounded text-sm hover:bg-gray-300 dark:hover:bg-slate-500"
                        >
                          取消
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
